<template>
	<section id="contact" class="section contact">
		<div class="container contact__inner">
			<div class="contact__main">
				<header v-reveal class="section__head">
					<p class="section__eyebrow">{{ t('contact.eyebrow') }}</p>
					<h2 class="section__title">{{ t('contact.title') }}</h2>
					<p class="section__subtitle">{{ t('contact.subtitle') }}</p>
				</header>

				<div v-reveal class="contact__grid">
					<div class="contact__item">
						<span class="contact__label marker">{{ t('contact.labels.location') }}</span>
						<div class="contact__value">
							<a
								:href="CONTACT_INFO.mapUrl"
								target="_blank"
								rel="noopener"
							>{{ t('contact.address-1') }}</a>
							<span class="contact__value-line">{{ t('contact.address-2') }}</span>
						</div>
					</div>

					<div class="contact__item">
						<span class="contact__label marker">{{ t('contact.labels.phone') }}</span>
						<div class="contact__value">
							<a :href="`tel:${CONTACT_INFO.phone}`">
								<bdi dir="ltr">{{ CONTACT_INFO.phoneDisplay }}</bdi>
							</a>
						</div>
					</div>

					<div class="contact__item">
						<span class="contact__label marker">{{ t('contact.labels.email') }}</span>
						<div class="contact__value">
							<a :href="`mailto:${CONTACT_INFO.email}`">
								<bdi dir="ltr">{{ CONTACT_INFO.email }}</bdi>
							</a>
						</div>
					</div>

					<div class="contact__item">
						<span class="contact__label marker">{{ t('contact.labels.website') }}</span>
						<div class="contact__value">
							<a
								:href="CONTACT_INFO.websiteUrl"
								target="_blank"
								rel="noopener"
							><bdi dir="ltr">{{ CONTACT_INFO.website }}</bdi></a>
						</div>
					</div>

					<div class="contact__item">
						<span class="contact__label marker">{{ t('contact.labels.linkedin') }}</span>
						<div class="contact__value">
							<a
								:href="CONTACT_INFO.linkedinUrl"
								target="_blank"
								rel="noopener"
							><bdi dir="ltr">{{ CONTACT_INFO.linkedin }}</bdi></a>
						</div>
					</div>
				</div>

				<div v-reveal class="contact__actions">
					<a :href="`tel:${CONTACT_INFO.phone}`" class="btn btn--gold">
						<app-icon name="call" />
						<span>{{ t('contact.actions.call') }}</span>
					</a>

					<a
						:href="CONTACT_INFO.whatsappUrl"
						class="btn btn--ghost"
						target="_blank"
						rel="noopener"
					>
						<app-icon name="whatsapp" />
						<span>{{ t('contact.actions.whatsapp') }}</span>
					</a>

					<a :href="`mailto:${CONTACT_INFO.email}`" class="btn btn--ghost">
						<app-icon name="email" />
						<span>{{ t('contact.actions.email') }}</span>
					</a>

					<a
						:href="vCardUrl"
						class="btn btn--ghost"
						download="beetronix.vcf"
					>
						<app-icon name="vcard" />
						<span>{{ t('contact.actions.vcard') }}</span>
					</a>
				</div>
			</div>

			<figure class="contact__art" aria-hidden="true">
				<img
					:src="assetUrl('contact-phone.webp')"
					alt=""
					width="515"
					height="2442"
					loading="lazy"
				>
			</figure>
		</div>
	</section>
</template>

<script setup>
import { onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import AppIcon from '@/components/AppIcon.vue'
import { CONTACT_INFO } from '@/helpers/constants'
import { assetUrl, createVCardUrl } from '@/helpers/functions'

defineOptions({ name: 'ContactSection' })

const { t } = useI18n()

// one-tap "save contact", for visitors arriving from the QR code on the card
const vCardUrl = createVCardUrl()

onBeforeUnmount(() => URL.revokeObjectURL(vCardUrl))
</script>

<style scoped lang="scss">
.contact {
	overflow: hidden;
	background: radial-gradient(90% 120% at 100% 0%, #fbfbfb, #eeeef0);

	@include rtl {
		background: radial-gradient(90% 120% at 0% 0%, #fbfbfb, #eeeef0);
	}
}

.contact__inner {
	display: flex;
	gap: clamp(20px, 4vw, 60px);
	// stretch, so the handset column can span the section's full height
	align-items: stretch;

	@include below($bp-tablet) {
		flex-direction: column-reverse;
	}
}

.contact__main {
	flex: 1;
	min-width: 0;
}

// the handset hangs from the very top edge of the section, as it does on the
// last page of the printed profile — so it cancels the section's top padding
.contact__art {
	position: relative;
	margin: calc(-1 * var(--section-pad-y)) 0;
	flex: none;
	width: clamp(140px, 19vw, 240px);

	// taken out of the flow: the render is 1:4.7, and in-flow it would drive the
	// section height and leave a big void under the text
	img {
		position: absolute;
		top: 0;
		inset-inline: 0;
		width: auto;
		max-width: 100%;
		height: 100%;
		margin-inline: auto;
		object-fit: contain;
		object-position: top center;
		filter: drop-shadow(0 24px 46px rgba(160, 120, 0, 0.22));
	}

	@include below($bp-tablet) {
		width: min(160px, 45%);
		margin-block-end: 0;
		margin-inline: auto;

		img {
			position: static;
			width: 100%;
			height: auto;
			max-height: 320px;
		}
	}
}

.contact__grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(min(100%, 230px), 1fr));
	gap: clamp(18px, 2.4vw, 30px);
}

.contact__label {
	display: inline-block;
	margin-bottom: 10px;
	font-size: 0.74rem;
	font-weight: 700;
	letter-spacing: 0.16em;
	text-transform: uppercase;

	@include arabic {
		letter-spacing: 0.04em;
	}
}

.contact__value {
	font-size: 1.02rem;
	font-weight: 500;
	line-height: 1.6;
	color: $color-ink;

	a {
		border-bottom: 1px solid transparent;
		transition: color 0.2s, border-color 0.2s;

		&:hover {
			color: $color-gold-deep;
			border-bottom-color: $color-gold-deep;
		}
	}
}

.contact__value-line {
	display: block;
	font-size: 0.96rem;
	font-weight: 400;
	color: $color-body;
}

.contact__actions {
	margin-top: clamp(30px, 4vw, 46px);
	display: flex;
	gap: 12px;
	flex-wrap: wrap;

	@include below($bp-mobile) {
		.btn {
			width: auto;
			flex: 1 1 calc(50% - 12px);
			justify-content: center;
		}
	}
}
</style>
